/**
 * "Synced from <host>" (HF2). The host's name, logo and monogram come from Console discovery; an
 * owner discovery doesn't list reads "Synced from a connected product". Never a name in code.
 */
import { Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HostOwner } from '@/api/endpoints/interplayPeople';
import { brandFor, hostName, type HostBrand } from './model';

function Mark({ brand }: { brand: HostBrand }) {
  if (brand.logoUrl) {
    return (
      <Box
        component="img"
        src={brand.logoUrl}
        alt=""
        referrerPolicy="no-referrer"
        sx={{ width: 16, height: 16, borderRadius: '4px', objectFit: 'contain', bgcolor: '#fff', flex: 'none' }}
      />
    );
  }
  if (!brand.monogram) return null;
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 16, height: 16, borderRadius: '4px', flex: 'none', display: 'grid', placeItems: 'center',
        fontSize: 9, fontWeight: 800, lineHeight: 1,
        bgcolor: brand.colour ?? 'action.selected', color: brand.colour ? '#fff' : 'text.primary',
      }}
    >
      {brand.monogram}
    </Box>
  );
}

export default function SyncedChip({
  owners,
  brands,
  size = 'small',
}: {
  owners: HostOwner[];
  brands: Record<string, HostBrand>;
  size?: 'small' | 'tiny';
}) {
  const { t } = useTranslation();
  const codes = Array.from(new Set(owners.map((o) => o.product_code)));
  if (codes.length === 0) return null;
  const first = brandFor(codes[0], brands);
  const names = codes.map((c) => hostName(t, brandFor(c, brands)));
  const label = t('interplay_people.synced_from', { host: names.join(', ') });
  return (
    <Tooltip title={t('interplay_people.synced_tip', { host: names.join(', ') })}>
      <Box
        component="span"
        data-testid="synced-chip"
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%',
          px: 0.75, py: size === 'tiny' ? 0 : 0.25, borderRadius: 99,
          border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
          fontSize: size === 'tiny' ? 11 : 12, fontWeight: 600, color: 'text.secondary', lineHeight: 1.6,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle',
        }}
      >
        <Mark brand={first} />
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
      </Box>
    </Tooltip>
  );
}
